import { CheckboxTree } from '@lib/components/CheckboxTree'
import type { CheckboxTreeState } from '@lib/components/CheckboxTree/types'
import { Button } from '@octopus-do/octopus-storybook'
import { ArrowRight } from '@octopus-do/octopus-storybook/icons'
import { selectCrawlImportPreparing } from '@store/crawlImport'
import { ErrorNotification } from '@storeOld/reducers/notification'
import cx from 'classnames'
import { type FC, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useFeatureFlags, useToggle } from '@/hooks'
import { type ImportSitemapData, WarningIconType } from '@/types'
import type { ModalProps } from '@/types/modal'
import { actionUrls } from '../../../TextWithAction'
import { Warning } from '../../../Warning'
import CrawlerStatusLogo from './CrawlerStatusLogo'
import css from './ImportSelectPages.module.scss'
import { buildTreeData, convertTreeToList } from './utils'

type Props = ModalProps & {
  data: ImportSitemapData
  handleCommit: (result: ImportSitemapData) => Promise<void>
  limitExceeded?: boolean
  timeoutExceeded?: boolean
}

const ImportSelectPages: FC<Props> = ({
  closeButton,
  data,
  handleCommit,
  limitExceeded,
  timeoutExceeded,
}) => {
  const dispatch = useDispatch()
  const urls = data.data.map(({ url }) => url)
  // A crawl that carried blocks was an "AI blocks" one, and those stop earlier on a
  // free plan (OCT-5091). Read off the pages themselves: a job reopened from the
  // crawler list no longer knows which mode started it.
  const withBlocks = data.data.some((page) => page.blocks !== undefined)

  const { crawler: featureFlags } = useFeatureFlags()
  const { userHasMaximumLimit } = featureFlags

  const [state, setState] = useState(
    convertTreeToList(buildTreeData(urls), true)
  )
  const [commitPending, toggleCommitPendingStatus] = useToggle()
  // The blocks being parsed into the new project once Continue is pressed (OCT-5101).
  const preparing = useSelector(selectCrawlImportPreparing)

  const handleUpgradeClick = () => {
    sendGtmEvent(
      GtmTriggerName.UPGRADE_CRAWLER,
      'Click on Upgrade to 3000 on the select pages screen'
    )

    window.open(actionUrls.UPGRADE_URL, '_blank')
  }

  const handleChangeTree = (newState: CheckboxTreeState) => {
    sendGtmEvent(
      GtmTriggerName.CRAWL_FILTER_CHECK,
      'Click on checkbox to filter crawler tree in crawler modal'
    )

    return setState(newState)
  }

  const handleSubmit = () => {
    sendGtmEvent(
      GtmTriggerName.CRAWL_FILTER_CONTINUE,
      'Click to continue in crawler modal'
    )

    const checkedItems = Array.from(state.values()).filter(
      (item) => item.checked
    )
    const filteredData = data.data.filter(({ url }) =>
      checkedItems.some((item) => url === item.value)
    )

    toggleCommitPendingStatus(true)
    handleCommit({
      ...data,
      data: filteredData,
    }).catch((error: Error) => {
      // Landing in an open project can be refused — read-only project, no tab — and the
      // refusal has to be visible: the button just stopping its spinner reads as nothing
      // happening, and the user presses Continue again.
      dispatch(ErrorNotification(error.message))
      toggleCommitPendingStatus(false)
    })
  }

  const selected = Array.from(state.values()).filter(
    (item) => item.value && item.checked
  ).length

  const nonCrawledPagesCount = data.details
    ? data.details.countSkippedByTimeout +
      data.details.countFailed +
      data.details.countSizeExceeded +
      data.details.countAbsent +
      data.details.countDenied +
      (data.details.countDomainNotFound ?? 0)
    : 0

  const isWarningVisible =
    limitExceeded || timeoutExceeded || nonCrawledPagesCount > 0

  return (
    <div className={css.container}>
      <div className={cx(css.heading)}>
        <CrawlerStatusLogo status="success" />
        <div className={css.title}>Crawl complete</div>
        <div className={css.close}>{closeButton}</div>
      </div>

      {isWarningVisible && (
        <div className={css.infoWrapper}>
          {timeoutExceeded && (
            <Warning
              iconType={WarningIconType.EXCLAMATION}
              url={actionUrls.HELP_URL}
              text="Read more"
            >
              Crawler was stopped due to set time-out limit, and the sitemap may
              be incomplete.
            </Warning>
          )}

          {nonCrawledPagesCount > 0 && (
            <Warning
              iconType={WarningIconType.EXCLAMATION}
              url={actionUrls.CRAWLER_ERRORS_DESCRIPTIONS}
              text="Read more"
            >
              {nonCrawledPagesCount}&nbsp;
              {nonCrawledPagesCount === 1 ? 'page' : 'pages'} can't be crawled,
              the sitemap may be incomplete.
            </Warning>
          )}

          {limitExceeded &&
            (userHasMaximumLimit ? (
              <Warning
                iconType={WarningIconType.INFO}
                url={actionUrls.HELP_URL}
                text="Help"
              >
                Limited to 3000 pages.
              </Warning>
            ) : (
              <Warning
                iconType={WarningIconType.INFO}
                actionFn={handleUpgradeClick}
                text="Upgrade to 3000"
              >
                Limited to {withBlocks ? 150 : 300} pages.
              </Warning>
            ))}
        </div>
      )}

      <div className={css.treeTitle}>Select pages for import:</div>

      <CheckboxTree
        state={state}
        onChange={handleChangeTree}
        className={css.tree}
      />

      <div className={css.footer}>
        <Button
          label="Continue"
          right={ArrowRight}
          disabled={!selected}
          loading={commitPending}
          onClick={handleSubmit}
        />
        {preparing && (
          <div className={css.preparing}>
            Preparing blocks… {preparing.done} of {preparing.total}
          </div>
        )}
      </div>
    </div>
  )
}

export default ImportSelectPages
